import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from "react-intl";

//Helpers
import { OrderData } from 'Core/data/Helpers';

//Components
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import {
    SearchInput,
    IconButton,
    Button,
    Modal,
    Input,
    Form,
} from '@gpstrackit/gtc-react-controls';
import {
    maintenanceMSTDeleteServiceItems,
    maintenanceMSTCreateServiceItem,
    maintenanceMSTUpdateServiceItem,
    maintenanceMSTGetServiceItems,
    reset,
} from './redux/slice'

//Styles
import './styles.scss';
const ManageServiceItems = (props) => {
    const manageTasksFormRef = Form.useForm()
    const dispatch = useDispatch();
    const { messages } = useIntl();

    //Local States
    const [openCreateEditServiceItems, setOpenCreateEditServiceItems] = useState(false);
    const [currentServiceItemDelete, setCurrentServiceItemDelete] = useState(null);
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [editServiceItem, setEditServiceItem] = useState(null);
    const [search, setSearch] = useState('');
    const [serviceItem, setServiceItem] = useState([]);

    //Redux States
    const {
        loading,
        serviceItems,
        loadingDelete,
        deleteServiceItems,
        loadingNewServiceItems,
        newServiceItems,
        loadingUpdateServiceItem,
        updateServiceItem: updateServiceItemResponse,
        open,
        error
    } = useSelector(state => state.maintenanceManageServiceTasksRedux)

    useEffect(() => {
        setServiceItem(OrderData(serviceItems || [], 'name'));
    }, [serviceItems]);

    useEffect(() => {
        if (open) dispatch(maintenanceMSTGetServiceItems(''));
    }, [open]);

    useEffect(() => {
        if (openModalDelete && deleteServiceItems) {
            dispatch(maintenanceMSTGetServiceItems(''));
            setOpenModalDelete(false);
            setSearch('');
            NotificationManager.success(
                messages['success'],
                messages['serviceItemsTab_deleteConfirmationSuccessfully'],
                3000,
                null,
                null,
                'success'
            );
        }
    }, [deleteServiceItems])

    useEffect(() => {
        if (openCreateEditServiceItems && newServiceItems && newServiceItems.id) {
            NotificationManager.success(
                messages['success'],
                messages['serviceItemsTab_createdNewServiceItem'],
                3000,
                null,
                null,
                'success'
            );
            handleServiceItemModal();
            dispatch(maintenanceMSTGetServiceItems(''));
        }
    }, [newServiceItems])

    useEffect(() => {
        if (openCreateEditServiceItems && updateServiceItemResponse && updateServiceItemResponse.id) {
            NotificationManager.success(
                messages['success'],
                messages['serviceItemsTab_updatedSuccessfully'],
                3000,
                null,
                null,
                'success'
            );
            handleServiceItemModal();
            dispatch(maintenanceMSTGetServiceItems(''));
        }
    }, [updateServiceItemResponse]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                error?.error || messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }))
        }
    }, [error])

    useEffect(() => {
        if (editServiceItem) {
            setTimeout(() => manageTasksFormRef.setValues({ serviceLabel: editServiceItem?.name || '' }), 100);
        }
    }, [editServiceItem])

    const handleDeleteServiceItem = (serviceItem) => {
        setOpenModalDelete(true);
        setCurrentServiceItemDelete(serviceItem);
    }

    const handleServiceItemModal = () => {
        setOpenCreateEditServiceItems(false);
        setEditServiceItem(null)
    }

    const onSearchText = useCallback((newSearchText) => {
        setSearch(newSearchText);

        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }

        window.typingTime = setTimeout(() => {
            dispatch(maintenanceMSTGetServiceItems(newSearchText))
        }, 500);
    });

    const renderItems = () => (
        <Fragment>
            {serviceItem &&
                serviceItem.map((serviceItem) => {
                    return (
                        <div key={serviceItem.id} className="content-service-item">
                            <div>
                                <span title={serviceItem?.name}>
                                    {serviceItem.name && serviceItem.name.substr(0, 34)}{serviceItem.name && serviceItem.name.length > 35 && "..."}
                                </span>
                            </div>
                            <div style={{ display: "flex" }}>
                                <IconButton
                                    name="edit"
                                    tooltip={messages['edit']}
                                    onClick={() => {
                                        setEditServiceItem({ ...serviceItem });
                                        setOpenCreateEditServiceItems(true);
                                    }}
                                    icon="edit"
                                    size="small"
                                />
                                <IconButton
                                    name="delete"
                                    tooltip={messages['delete']}
                                    onClick={() => handleDeleteServiceItem(serviceItem)}
                                    icon="delete"
                                    size="small"
                                />
                            </div>
                        </div>
                    )
                })}
        </Fragment>
    )

    return (
        <Fragment>
            <Modal
                title={messages['serviceItemsTab_Title']}
                open={open}
                handleClose={() => dispatch(reset())}
                position="right"
                size="small"
                loading={loading || loadingDelete}
                footer={(
                    <div>
                        <Button onClick={() => dispatch(reset())} variant='secondary' style={{ marginRight: "5px" }}>
                            {messages['cancel']}
                        </Button>
                        <Button onClick={() => setOpenCreateEditServiceItems(true)} className="btn-blue">
                            {messages['new']}
                        </Button>
                    </div>
                )}
            >
                <div className="manage-tasks-modal-container">
                    <div style={{ marginBottom: "10px" }}>
                        <SearchInput
                            searchText={search}
                            onSearchText={onSearchText}
                            placeholder={messages['search']}
                        />
                    </div>
                    {renderItems()}
                </div>
                <NotificationContainer />
            </Modal>
            <Modal
                title={editServiceItem ? editServiceItem.name : messages['serviceItemsTab_createServiceItem']}
                open={openCreateEditServiceItems}
                handleClose={() => handleServiceItemModal()}
                position="right"
                size="small"
                loading={loadingNewServiceItems || loadingUpdateServiceItem}
                footer={(
                    <div>
                        <Button
                            onClick={() => handleServiceItemModal()}
                            variant="secondary"
                            style={{ marginRight: "5px" }}
                        >
                            {messages['cancel']}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => manageTasksFormRef.submit()}
                            isDisabled={loadingNewServiceItems}
                        >
                            {messages['save']}
                        </Button>
                    </div>
                )}
            >
                <div className="manage-tasks-modal-container">
                    <Form
                        ref={manageTasksFormRef.ref}
                        onValidSubmit={(values) => {
                            let serviceItem = { name: values?.serviceLabel };
                            if (editServiceItem?.id) {
                                serviceItem = {
                                    ...serviceItem,
                                    id: editServiceItem?.id
                                }
                                dispatch(maintenanceMSTUpdateServiceItem(serviceItem))
                            } else {
                                dispatch(maintenanceMSTCreateServiceItem(serviceItem))
                            }
                        }}
                    >
                        <Form.Item
                            name="serviceLabel"
                            label={messages['serviceItemsTab_serviceItemLabel']}
                            rules={[{ required: true, message: messages['required_field'] }]}
                        >
                            <Input
                                id="serviceLabel"
                                type="text"
                                placeholder={messages['serviceItemsTab_serviceItemInputPlaceholder']}
                                autoFocus
                                maxLength={40}
                            />
                        </Form.Item>
                    </Form>
                </div>
                <NotificationContainer />
            </Modal>
            <Modal
                title={`${messages['serviceItemsTab_delete']} "${currentServiceItemDelete && currentServiceItemDelete?.name}"`}
                open={openModalDelete}
                handleClose={() => setOpenModalDelete(false)}
                loading={loadingDelete}
                size="small"
                footer={(
                    <div>
                        <Button
                            variant="secondary"
                            onClick={() => setOpenModalDelete(false)}
                            style={{ marginRight: "5px" }}
                        >
                            {messages['cancel']}
                        </Button>
                        <Button
                            variant="warning"
                            isDisabled={loadingDelete}
                            onClick={() => {
                                if (currentServiceItemDelete?.id) {
                                    dispatch(maintenanceMSTDeleteServiceItems(currentServiceItemDelete?.id))
                                }
                            }}
                        >
                            {messages['delete']}
                        </Button>
                    </div>
                )}
            >
                <div className="manage-tasks-modal-container">
                    <p style={{ overflowWrap: 'anywhere' }}>
                        {messages['serviceItemsTab_deleteConfirmation']}
                        {` "${currentServiceItemDelete && currentServiceItemDelete?.name}" ?`}
                    </p>
                </div>
                <NotificationContainer />
            </Modal>
        </Fragment>
    )

}

export default ManageServiceItems;